import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { driver, type Driver } from "driver.js";
import "driver.js/dist/driver.css";
import { GraduationCap, X } from "lucide-react";
import { toast } from "sonner";

import { onDemoEvent } from "@/lib/demo/demo-store";
import { DEMO_TRIP_ID } from "@/lib/demo/demo-trip";
import { abandonTour, endTour, goToStep, useTourState } from "@/lib/onboarding/tour-state";
import { TOUR_STEPS, targetSelector, type TourStep } from "@/lib/onboarding/tour-steps";
import { cn } from "@/lib/utils";
import { queryKeys } from "@/lib/api";

/**
 * Onboarding tour controller. Mounted once inside the authed layout
 * (router.tsx) and renders nothing until startTour() flips the store.
 *
 * Walks TOUR_STEPS across routes: navigates to each step's path, waits
 * for its `[data-tour]` target to mount, then either spotlights it with
 * driver.js or — for hands-on "task" steps — shows a slim hint bar and
 * waits for the sample trip to report the action.
 *
 * driver.js is used in single-highlight mode (not its built-in steps)
 * because our steps span routes and wait on user actions it can't see.
 */
export function OnboardingTour() {
  const tour = useTourState();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const driverRef = useRef<Driver | null>(null);

  const step: TourStep | null = tour.active ? (TOUR_STEPS[tour.stepIndex] ?? null) : null;
  const total = TOUR_STEPS.length;
  // Tracks the path we last navigated to for a step, so a user who
  // wanders off mid-step sees "paused" instead of being yanked back.
  const onStepPath = !step?.path || location.pathname === step.path;

  const destroyDriver = useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = null;
  }, []);

  const finish = useCallback(
    (outcome: "completed" | "skipped", to = "/dashboard") => {
      destroyDriver();
      endTour(outcome);
      qc.removeQueries({ queryKey: queryKeys.trip(DEMO_TRIP_ID) });
      navigate(to, { replace: true });
    },
    [destroyDriver, navigate, qc],
  );

  const goTo = useCallback(
    (index: number) => {
      if (index >= total) finish("completed", "/trip/create");
      else goToStep(index);
    },
    [finish, total],
  );

  /** The user did the step's action (or it has none): continue in order. */
  const next = useCallback(() => goTo(tour.stepIndex + 1), [goTo, tour.stepIndex]);

  /**
   * The Next button. On a hands-on step it skips the action and its
   * follow-up steps (`skipTo`); elsewhere it's the same as `next`.
   */
  const skip = useCallback(() => {
    if (!step?.skipTo) return next();
    if (step.skipPath) navigate(step.skipPath, { replace: true });
    goTo(TOUR_STEPS.findIndex((s) => s.id === step.skipTo));
  }, [goTo, navigate, next, step]);

  // Keep the latest callbacks reachable from driver.js / DOM handlers,
  // which are created once per highlight and would otherwise go stale.
  const nextRef = useRef(next);
  const skipRef = useRef(skip);
  const finishRef = useRef(finish);
  useEffect(() => {
    nextRef.current = next;
    skipRef.current = skip;
    finishRef.current = finish;
  }, [next, skip, finish]);

  // Navigate to the step's route when the step starts (not on every
  // location change — see onStepPath). Leftover toasts ("Added to the
  // timeline.") share the top edge with the hint bar, so clear them too.
  useEffect(() => {
    if (!tour.active) return;
    toast.dismiss();
    if (!step?.path) return;
    if (window.location.pathname !== step.path) navigate(step.path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour.active, tour.stepIndex]);

  // Advance on sample-trip events (task steps).
  useEffect(() => {
    if (!step?.advanceOnEvent) return;
    const wanted = step.advanceOnEvent;
    return onDemoEvent((event) => {
      if (event === wanted) nextRef.current();
    });
  }, [step]);

  // Spotlight: wait for the target, then highlight it.
  useEffect(() => {
    if (!step || step.kind !== "spotlight" || !onStepPath) {
      destroyDriver();
      return;
    }
    const controller = new AbortController();
    const selector = targetSelector(step);
    void (selector ? waitForElement(selector, 4000, controller.signal) : Promise.resolve(null)).then(
      (el) => {
        if (controller.signal.aborted) return;
        // Tapping the highlighted element does the action and continues;
        // the Next button (below) skips it instead. Handled here rather
        // than with driver.js' advanceOnClick, which shares Next's handler.
        if (step.advanceOnTargetClick && el) {
          el.addEventListener("click", () => nextRef.current(), {
            once: true,
            signal: controller.signal,
          });
        }
        const drv =
          driverRef.current ??
          driver({
            animate: true,
            allowClose: false,
            overlayClickBehavior: () => undefined,
            overlayColor: "rgb(20, 15, 10)",
            overlayOpacity: 0.62,
            stagePadding: 6,
            stageRadius: 16,
            popoverOffset: 12,
            smoothScroll: true,
            popoverClass: "travelix-tour",
          });
        driverRef.current = drv;
        drv.highlight({
          element: el ?? undefined,
          popover: {
            title: step.title,
            description: step.body,
            side: step.side ?? "bottom",
            align: "center",
            showButtons: ["next"],
            nextBtnText: step.nextLabel ?? "Next",
            // The last step needs the room for its two buttons.
            showProgress: tour.stepIndex < total - 1,
            progressText: `${tour.stepIndex + 1} of ${total}`,
            onNextClick: () => skipRef.current(),
            onPopoverRender: (popover) => {
              // Secondary action: "Skip tour" mid-way; on the last step
              // the tour is done either way, so it just goes home.
              const isLast = tour.stepIndex === total - 1;
              const secondary = document.createElement("button");
              secondary.type = "button";
              secondary.className = "driver-popover-footer-btn travelix-tour-skip";
              secondary.textContent = isLast ? "Maybe later" : "Skip tour";
              secondary.addEventListener("click", () =>
                finishRef.current(isLast ? "completed" : "skipped"),
              );
              popover.footerButtons.prepend(secondary);
            },
          },
        });
      },
    );
    return () => controller.abort();
  }, [step, onStepPath, destroyDriver, tour.stepIndex, total]);

  // Task steps: gently pulse the element the user should reach for.
  useEffect(() => {
    const selector = step?.kind === "task" ? targetSelector(step) : null;
    if (!selector) return;
    const controller = new AbortController();
    let el: Element | null = null;
    void waitForElement(selector, 4000, controller.signal).then((found) => {
      if (controller.signal.aborted || !found) return;
      el = found;
      el.classList.add("tour-pulse");
    });
    return () => {
      controller.abort();
      el?.classList.remove("tour-pulse");
    };
  }, [step]);

  // The controller unmounts when the user signs out (it lives in the authed
  // layout). Drop the tour and the sample trip too, so the next account on
  // this device doesn't inherit them. No done-flag is written — the tour
  // wasn't finished.
  useEffect(
    () => () => {
      destroyDriver();
      abandonTour();
      qc.removeQueries({ queryKey: queryKeys.trip(DEMO_TRIP_ID) });
    },
    [destroyDriver, qc],
  );

  if (!step) return null;

  if (!onStepPath) {
    return (
      <TourHintBar
        title="Tutorial paused"
        body="Pick up where you left off, or skip it for now."
        actionLabel="Resume"
        onAction={() => navigate(step.path!)}
        onSkip={() => finish("skipped")}
      />
    );
  }

  if (step.kind === "task") {
    return (
      <TourHintBar
        title={step.title}
        body={step.body}
        progress={`${tour.stepIndex + 1} of ${total}`}
        onNext={skip}
        onSkip={() => finish("skipped")}
      />
    );
  }

  return null;
}

// ─── Hint bar (task + paused states) ─────────────────────────
/**
 * Non-blocking instruction strip pinned to the top of the screen. Sits
 * above sheets (z-50) so it stays visible while the user fills in the
 * add-event / add-expense forms.
 */
function TourHintBar({
  title,
  body,
  progress,
  actionLabel,
  onAction,
  onNext,
  onSkip,
}: {
  title: string;
  body: string;
  progress?: string;
  actionLabel?: string;
  onAction?: () => void;
  /** Shows a compact Next pill in the title row (kept to one row so the bar
   *  stays short — taller bars covered the Add-expense menu). */
  onNext?: () => void;
  onSkip: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] px-3"
      style={{ paddingTop: "calc(var(--safe-top) + 8px)" }}
    >
      <div
        className={cn(
          "pointer-events-auto mx-auto flex max-w-md items-start gap-3 rounded-xl",
          "border border-primary/30 bg-bg-elevated px-4 py-3 shadow-warm-3",
          "animate-in slide-in-from-top-2 duration-200",
        )}
      >
        <GraduationCap className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="m-0 text-[14px] font-semibold text-ink">
              {title}
              {progress ? (
                <span className="ml-2 text-[12px] font-medium text-ink-subtle">{progress}</span>
              ) : null}
            </p>
            {onNext ? (
              <button
                type="button"
                onClick={onNext}
                className="shrink-0 rounded-full bg-primary px-3 py-1 text-[12px] font-semibold text-white hover:bg-primary-hover"
              >
                Next
              </button>
            ) : null}
          </div>
          <p className="m-0 mt-0.5 text-[13px] leading-snug text-ink-muted">{body}</p>
          {actionLabel && onAction ? (
            <button
              type="button"
              onClick={onAction}
              className="mt-2 text-[13px] font-semibold text-primary hover:underline"
            >
              {actionLabel}
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onSkip}
          aria-label="Skip tutorial"
          className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ink-subtle hover:bg-bg-sunken hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────
/** Resolve with the first element matching `selector`, or null after `timeoutMs`. */
function waitForElement(
  selector: string,
  timeoutMs: number,
  signal: AbortSignal,
): Promise<Element | null> {
  return new Promise((resolve) => {
    const found = document.querySelector(selector);
    if (found) return resolve(found);
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) done(el);
    });
    const timer = window.setTimeout(() => done(null), timeoutMs);
    const done = (el: Element | null) => {
      observer.disconnect();
      window.clearTimeout(timer);
      resolve(el);
    };
    signal.addEventListener("abort", () => done(null));
    observer.observe(document.body, { childList: true, subtree: true });
  });
}
